import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { Icon, type IconName } from "../components/Icon.tsx";
import { Shell } from "../components/Shell.tsx";
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Skeleton,
  StatusBadge,
  type Tone,
} from "../components/ui.tsx";
import { locale, t, tn, type MessageKey } from "../i18n/index.ts";
import { action } from "../lib/api.ts";
import { useLiveAction } from "../lib/data.ts";
import { clock, day, money, parseMoney, todayIso } from "../lib/format.ts";
import { usePlaces } from "../lib/places.tsx";
import { device, useSession } from "../lib/session.tsx";
import { refreshTeam } from "../lib/team.ts";
import { useNow, useUi } from "../lib/ui-state.tsx";
import { useAction, useSave } from "./manage/common.tsx";
import { downloadCsv, isDay, Kpi, plain, Table } from "./Reports.tsx";
import {
  liveSwapOf,
  MarketCard,
  OfferDialog,
  OpenShiftDialog,
  PlanningBar,
  PositionsDialog,
  PositionTag,
  SwapBadge,
  SwapsDialog,
  useMarket,
  type OpenShiftRow,
  type PlanningIssues,
  type Position,
} from "./TeamPlanning.tsx";

/**
 * The team screen: my shifts and the clock for everyone; who is in today, the rota, the
 * hours, the tips and the payroll for those allowed to see them.
 */

type Tab = "me" | "today" | "rota" | "hours" | "tips" | "payroll";
const TABS: { key: Tab; icon: IconName; permission: string }[] = [
  { key: "me", icon: "clock", permission: "team.self" },
  { key: "today", icon: "users", permission: "team.view" },
  { key: "rota", icon: "calendar", permission: "team.manage" },
  { key: "hours", icon: "list", permission: "team.payroll" },
  { key: "tips", icon: "cash", permission: "team.view" },
  { key: "payroll", icon: "book", permission: "team.payroll" },
];

interface PlannedShift {
  id: string;
  user: string;
  location: string;
  location_name: string;
  day: string;
  start: string;
  end: string;
  label: string | null;
  position: string | null;
  note: string | null;
  state: "draft" | "published";
  from: string;
  to: string;
  minutes: number;
}

/** A table that scrolls sideways inside its card on a narrow phone instead of widening the page. */
const WideTable = (props: Parameters<typeof Table>[0]) => (
  // Focusable, so a keyboard can scroll it too.
  <div
    className="table-wrap"
    role="region"
    aria-label={props.head.filter(Boolean).join(", ")}
    tabIndex={0}
  >
    <Table {...props} />
  </div>
);

/** "7 h 30 min", "45 min", or a dash for nothing. */
export function hm(minutes: number): string {
  if (!minutes) return "—";
  if (minutes < 60) return t("time.minutes", { n: minutes });
  return minutes % 60
    ? t("time.hours", { h: Math.floor(minutes / 60), m: minutes % 60 })
    : t("team.h", { h: minutes / 60 });
}
const hours = (minutes: number) => (minutes / 60).toFixed(2);
const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const mondayOf = (d: string) =>
  addDays(d, -((new Date(`${d}T00:00:00Z`).getUTCDay() + 6) % 7));
const weekday = (d: string, style: "short" | "long" = "short") =>
  new Intl.DateTimeFormat(locale(), { weekday: style, timeZone: "UTC" }).format(
    new Date(`${d}T00:00:00Z`),
  );
const shortDay = (d: string) =>
  new Intl.DateTimeFormat(locale(), {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${d}T00:00:00Z`));
/** A date-time input's value ("2026-10-26T09:00") from an instant, in this device's time zone. */
const toLocalInput = (at: string | Date) => {
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function Team() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter((x) => can(x.permission));
  const tab = (
    tabs.some((x) => x.key === params.get("tab"))
      ? params.get("tab")
      : (tabs[0]?.key ?? "me")
  ) as Tab;
  const go = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === "me") p.delete("tab");
    else p.set("tab", k);
    for (const x of ["from", "to", "week", "p"]) p.delete(x);
    setParams(p, { replace: true });
  };
  return (
    <Shell>
      <div className="page team-page">
        <div className="page-head">
          <div>
            <h1>{t("team.title")}</h1>
            <p className="muted">{t(`team.sub_${tab}` as MessageKey)}</p>
          </div>
        </div>
        {tabs.length > 1 ? (
          <div
            className="segmented tabs team-tabs"
            role="tablist"
            aria-label={t("team.title")}
          >
            {tabs.map((x) => (
              <button
                key={x.key}
                role="tab"
                aria-selected={tab === x.key}
                className={tab === x.key ? "active" : ""}
                onClick={() => go(x.key)}
              >
                <Icon name={x.icon} size={18} />{" "}
                {t(`team.tab_${x.key}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
        {tab === "me" ? <Me /> : null}
        {tab === "today" ? <Today /> : null}
        {tab === "rota" ? <Rota /> : null}
        {tab === "hours" ? <Hours /> : null}
        {tab === "tips" ? <Tips /> : null}
        {tab === "payroll" ? <Payroll /> : null}
      </div>
    </Shell>
  );
}

// ---- me ----------------------------------------------------------------------------------

interface MeData {
  now: string;
  today: string;
  open: {
    id: string;
    in_at: string;
    location: string;
    location_name: string;
    shift: PlannedShift | null;
  } | null;
  upcoming: PlannedShift[];
  week: {
    worked_minutes: number;
    planned_minutes: number;
    days: {
      id: string;
      day: string;
      in_at: string;
      out_at: string | null;
      break_minutes: number;
      minutes: number;
      location_name: string;
    }[];
  };
  tips: { week: number; owed: number; mode: "own" | "pool" } | null;
}

function Me() {
  const { fail, toast } = useUi();
  const { here, multi } = usePlaces();
  const now = useNow(30_000);
  const { data, reload } = useLiveAction<MeData>("team.me", {}, ["team"]);
  const market = useMarket();
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [offering, setOffering] = useState<PlannedShift | null>(null);

  const clockIn = async () => {
    setBusy(true);
    try {
      await action("team.clock_in", { location: here?.id ?? null });
      toast(t("team.clocked_in", { time: clock(new Date()) }));
      await reload();
      refreshTeam();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Skeleton h={220} r="var(--radius-lg)" />;
  const next = data.upcoming.find((s) => new Date(s.to).getTime() > now);
  const todays = data.upcoming.filter(
    (s) =>
      s.day === data.today ||
      (new Date(s.from).getTime() < now + 12 * 3_600_000 &&
        new Date(s.to).getTime() > now),
  );
  const workedNow = data.open
    ? Math.max(
        0,
        Math.floor((now - new Date(data.open.in_at).getTime()) / 60_000),
      )
    : 0;
  const later = data.upcoming.filter((s) => !todays.includes(s));

  return (
    <div className="team-me">
      <section
        className={`card clock-card${data.open ? " is-in" : ""}`}
        aria-live="polite"
      >
        <span className="clock-card-icon">
          <Icon name="clock" size={28} />
        </span>
        <div className="clock-card-text">
          {data.open ? (
            <>
              <strong className="clock-card-title">
                {t("team.in_since", { time: clock(data.open.in_at) })}
              </strong>
              <span>
                {hm(workedNow)}
                {multi ? ` · ${data.open.location_name}` : ""}
                {data.open.shift
                  ? ` · ${t("team.planned_until", { time: data.open.shift.end })}`
                  : ""}
              </span>
            </>
          ) : todays[0] ? (
            <>
              <strong className="clock-card-title">
                {t("team.shift_today", {
                  from: todays[0].start,
                  to: todays[0].end,
                })}
              </strong>
              <span>
                {[todays[0].label, multi ? todays[0].location_name : null]
                  .filter(Boolean)
                  .join(" · ") || t("team.not_in")}
              </span>
            </>
          ) : (
            <>
              <strong className="clock-card-title">
                {t("team.no_shift_today")}
              </strong>
              <span>
                {next
                  ? t("team.next_shift", {
                      day: `${weekday(next.day, "long")} ${shortDay(next.day)}`,
                      from: next.start,
                      to: next.end,
                    })
                  : t("team.no_upcoming")}
              </span>
            </>
          )}
        </div>
        {data.open ? (
          <Button
            variant="danger"
            size="xl"
            icon="logout"
            onClick={() => setLeaving(true)}
          >
            {t("team.clock_out")}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="xl"
            icon="clock"
            busy={busy}
            onClick={() => void clockIn()}
          >
            {t("team.clock_in")}
          </Button>
        )}
      </section>

      <div className="rep-kpis team-kpis">
        <Kpi
          label={t("team.k_week")}
          value={hm(data.week.worked_minutes)}
          hint={
            data.week.planned_minutes
              ? t("team.k_planned", { h: hm(data.week.planned_minutes) })
              : undefined
          }
        />
        {data.tips ? (
          <Kpi
            label={t("team.k_tips_week")}
            value={money(data.tips.week)}
            hint={data.tips.mode === "pool" ? t("team.pool_hint") : undefined}
          />
        ) : null}
        {data.tips ? (
          <Kpi
            label={t("team.k_tips_owed")}
            value={money(Math.max(0, data.tips.owed))}
            hint={t("team.owed_hint")}
          />
        ) : null}
      </div>

      <section className="card">
        <header className="card-head">
          <h2>{t("team.my_shifts")}</h2>
        </header>
        {data.upcoming.length === 0 ? (
          <p className="muted">{t("team.no_upcoming")}</p>
        ) : (
          <ul className="team-shift-list">
            {[...todays, ...later].map((s) => (
              <li key={s.id}>
                <span className="team-shift-day">
                  <strong>
                    {s.day === data.today ? t("team.today") : weekday(s.day)}
                  </strong>
                  <span className="muted">{shortDay(s.day)}</span>
                </span>
                <span className="team-shift-time">
                  {s.start}–{s.end}
                </span>
                <span className="muted team-shift-meta">
                  {[s.label, multi ? s.location_name : null, hm(s.minutes)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span className="team-shift-act">
                  {(() => {
                    const live = liveSwapOf(market.data, s.id);
                    if (live) return <SwapBadge state={live.state} />;
                    if (new Date(s.from).getTime() <= now) return null;
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="repeat"
                        onClick={() => setOffering(s)}
                        aria-label={t("plan.offer_shift_of", {
                          day: `${weekday(s.day, "long")} ${shortDay(s.day)}`,
                        })}
                      >
                        {t("plan.offer")}
                      </Button>
                    );
                  })()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <MarketCard market={market.data} multi={multi} reload={market.reload} />

      {data.week.days.length ? (
        <section className="card">
          <header className="card-head">
            <h2>{t("team.my_hours")}</h2>
          </header>
          <WideTable
            head={[
              t("team.c_day"),
              t("team.c_in_out"),
              t("team.c_break"),
              t("team.c_worked"),
            ]}
            left={[1]}
            rows={data.week.days.map((d) => ({
              key: d.id,
              cells: [
                `${weekday(d.day)} ${shortDay(d.day)}`,
                `${clock(d.in_at)}–${d.out_at ? clock(d.out_at) : t("team.now")}`,
                d.break_minutes ? hm(d.break_minutes) : "—",
                hm(d.minutes),
              ],
            }))}
          />
        </section>
      ) : null}

      {offering ? (
        <OfferDialog
          shift={offering}
          onClose={() => setOffering(null)}
          onDone={async () => {
            setOffering(null);
            await market.reload();
          }}
        />
      ) : null}
      {leaving && data.open ? (
        <ClockOutDialog
          since={data.open.in_at}
          onClose={() => setLeaving(false)}
          onDone={async (worked) => {
            setLeaving(false);
            toast(t("team.clocked_out", { worked: hm(worked) }));
            await reload();
            refreshTeam();
          }}
        />
      ) : null}
    </div>
  );
}

function ClockOutDialog({
  since,
  onClose,
  onDone,
}: {
  since: string;
  onClose: () => void;
  onDone: (worked: number) => void;
}) {
  const { run, busy } = useSave();
  const [brk, setBrk] = useState(0);
  const length = Math.max(
    0,
    Math.floor((Date.now() - new Date(since).getTime()) / 60_000),
  );
  const submit = () =>
    void run(async () => {
      const r = await action<{ worked_minutes: number }>("team.clock_out", {
        break_minutes: brk,
      });
      onDone(r.worked_minutes);
    });
  return (
    <Dialog
      title={t("team.clock_out")}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger"
            icon="logout"
            busy={busy}
            disabled={brk >= length && length > 0}
            onClick={submit}
          >
            {t("team.clock_out_now", { worked: hm(Math.max(0, length - brk)) })}
          </Button>
        </>
      }
    >
      <p className="muted">
        {t("team.out_summary", {
          from: clock(since),
          to: clock(new Date()),
          length: hm(length),
        })}
      </p>
      <Field label={t("team.break_taken")} hint={t("team.break_hint")}>
        <div
          className="segmented chips-row"
          role="radiogroup"
          aria-label={t("team.break_taken")}
        >
          {[0, 15, 30, 45, 60].map((m) => (
            <button
              key={m}
              role="radio"
              aria-checked={brk === m}
              className={brk === m ? "active" : ""}
              disabled={m >= length && m > 0}
              onClick={() => setBrk(m)}
            >
              {m ? t("time.minutes", { n: m }) : t("team.no_break")}
            </button>
          ))}
        </div>
      </Field>
    </Dialog>
  );
}

// ---- today -------------------------------------------------------------------------------

interface Board {
  now: string;
  working: {
    entry: string;
    user: string;
    name: string;
    roles: string[];
    location_name: string;
    in_at: string;
    minutes: number;
    long: boolean;
  }[];
  expected: {
    shift: string;
    user: string;
    name: string;
    start: string;
    end: string;
    label: string | null;
    location_name: string;
    state: "due" | "in" | "late" | "missed" | "done";
    late_minutes: number;
  }[];
}
const EXPECTED_LOOK: Record<
  Board["expected"][number]["state"],
  { tone: Tone; icon: IconName }
> = {
  due: { tone: "neutral", icon: "clock" },
  in: { tone: "ok", icon: "checkCircle" },
  late: { tone: "warn", icon: "alert" },
  missed: { tone: "danger", icon: "x" },
  done: { tone: "neutral", icon: "check" },
};

function Today() {
  const { here, multi } = usePlaces();
  const { data } = useLiveAction<Board>(
    "team.board",
    multi && here ? { location: here.id } : {},
    ["team"],
  );
  if (!data) return <Skeleton h={200} r="var(--radius-lg)" />;
  return (
    <div className="team-today">
      <section className="card">
        <header className="card-head">
          <h2>
            {t("team.working_now")}{" "}
            <span className="count-pill">{data.working.length}</span>
          </h2>
        </header>
        {data.working.length === 0 ? (
          <p className="muted">{t("team.nobody_in")}</p>
        ) : (
          <ul className="team-people">
            {data.working.map((w) => (
              <li key={w.entry}>
                <span className="avatar" aria-hidden="true">
                  {initials(w.name)}
                </span>
                <span className="team-people-text">
                  <strong>{w.name}</strong>
                  <span className="muted">
                    {t("team.in_since", { time: clock(w.in_at) })} ·{" "}
                    {hm(w.minutes)}
                    {multi && !here ? ` · ${w.location_name}` : ""}
                  </span>
                </span>
                {w.long ? (
                  <StatusBadge
                    tone="warn"
                    icon="alert"
                    label={t("team.long")}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="card">
        <header className="card-head">
          <h2>{t("team.expected_today")}</h2>
        </header>
        {data.expected.length === 0 ? (
          <p className="muted">{t("team.no_rota_today")}</p>
        ) : (
          <ul className="team-people">
            {data.expected.map((x) => (
              <li key={x.shift}>
                <span className="avatar" aria-hidden="true">
                  {initials(x.name)}
                </span>
                <span className="team-people-text">
                  <strong>{x.name}</strong>
                  <span className="muted">
                    {[
                      `${x.start}–${x.end}`,
                      x.label,
                      multi && !here ? x.location_name : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <StatusBadge
                  tone={EXPECTED_LOOK[x.state].tone}
                  icon={EXPECTED_LOOK[x.state].icon}
                  label={
                    x.state === "in" && x.late_minutes
                      ? t("team.s_in_late", { n: x.late_minutes })
                      : t(`team.s_${x.state}` as MessageKey)
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");

// ---- rota --------------------------------------------------------------------------------

interface Week {
  start: string;
  days: string[];
  today: string;
  people: { id: string; name: string; roles: string[]; hourly_rate: number }[];
  shifts: PlannedShift[];
  elsewhere: PlannedShift[];
  worked: { user: string; day: string; minutes: number }[];
  drafts: number;
  positions: Position[];
  open_shifts: OpenShiftRow[];
  issues: PlanningIssues;
}

function Rota() {
  const { here } = usePlaces();
  const [location, setLocation] = useState<string | null>(null);
  const loc = location ?? here?.id ?? null;
  // The week is per location: wait until this device's location is known.
  return loc ? (
    <RotaWeek loc={loc} setLocation={setLocation} />
  ) : (
    <Skeleton h={320} r="var(--radius-lg)" />
  );
}

function RotaWeek({
  loc,
  setLocation,
}: {
  loc: string;
  setLocation: (id: string) => void;
}) {
  const { places, multi, name: placeName } = usePlaces();
  const [params, setParams] = useSearchParams();
  const { fail, toast } = useUi();
  const week = isDay(params.get("week"))
    ? mondayOf(params.get("week")!)
    : mondayOf(todayIso());
  const { data, reload } = useLiveAction<Week>(
    "team.week",
    { start: week, location: loc },
    ["team"],
  );
  const [editing, setEditing] = useState<Partial<PlannedShift> | null>(null);
  const [openEditing, setOpenEditing] = useState<Partial<OpenShiftRow> | null>(
    null,
  );
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [swapsOpen, setSwapsOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const setWeek = (d: string) => {
    const p = new URLSearchParams(params);
    p.set("week", d);
    setParams(p, { replace: true });
  };
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
      await reload();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };
  const current = data && data.start === week ? data : null;
  const byCell = useMemo(() => {
    const m = new Map<string, PlannedShift[]>();
    for (const s of current?.shifts ?? [])
      m.set(`${s.user}|${s.day}`, [...(m.get(`${s.user}|${s.day}`) ?? []), s]);
    return m;
  }, [current]);
  const elsewhere = useMemo(() => {
    const m = new Map<string, PlannedShift[]>();
    for (const s of current?.elsewhere ?? [])
      m.set(`${s.user}|${s.day}`, [...(m.get(`${s.user}|${s.day}`) ?? []), s]);
    return m;
  }, [current]);
  const worked = useMemo(
    () =>
      new Map(
        (current?.worked ?? []).map((w) => [`${w.user}|${w.day}`, w.minutes]),
      ),
    [current],
  );
  const posById = useMemo(
    () => new Map((current?.positions ?? []).map((p) => [p.id, p])),
    [current],
  );
  const total = (user: string) =>
    (current?.shifts ?? [])
      .filter((s) => s.user === user)
      .reduce((a, s) => a + s.minutes, 0);
  const cost = (current?.people ?? []).reduce(
    (a, p) => a + Math.round((total(p.id) * p.hourly_rate) / 60),
    0,
  );

  return (
    <div className="team-rota">
      <div className="toolbar">
        <div className="toolbar-group">
          <Button
            variant="ghost"
            icon="back"
            aria-label={t("team.prev_week")}
            onClick={() => setWeek(addDays(week, -7))}
          />
          <strong className="team-week-label">
            {t("team.week_of", {
              from: shortDay(week),
              to: day(addDays(week, 6)),
            })}
          </strong>
          <Button
            variant="ghost"
            icon="chevronRight"
            aria-label={t("team.next_week")}
            onClick={() => setWeek(addDays(week, 7))}
          />
          {week !== mondayOf(todayIso()) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWeek(mondayOf(todayIso()))}
            >
              {t("team.this_week")}
            </Button>
          ) : null}
        </div>
        {multi ? (
          <div
            className="segmented"
            role="radiogroup"
            aria-label={t("team.location")}
          >
            {places.map((p) => (
              <button
                key={p.id}
                role="radio"
                aria-checked={loc === p.id}
                className={loc === p.id ? "active" : ""}
                onClick={() => setLocation(p.id)}
              >
                <Icon name="store" size={16} /> {p.name}
              </button>
            ))}
          </div>
        ) : null}
        <div className="toolbar-group toolbar-end">
          <Button
            variant="ghost"
            icon="layers"
            onClick={() => setPositionsOpen(true)}
          >
            {t("plan.positions")}
          </Button>
          <Button
            icon="inbox"
            onClick={() =>
              setOpenEditing({
                location: loc,
                day:
                  current?.today && current.days.includes(current.today)
                    ? current.today
                    : week,
              })
            }
          >
            {t("plan.add_open")}
          </Button>
          <Button
            icon="copy"
            busy={busy === "copy"}
            onClick={() =>
              void run("copy", async () => {
                const r = await action<{ copied: number; skipped: number }>(
                  "team.copy_week",
                  { from: addDays(week, -7), to: week, location: loc },
                );
                toast(
                  r.skipped
                    ? t("team.copied_skipped", {
                        n: r.copied,
                        skipped: r.skipped,
                      })
                    : tn("team.copied", r.copied),
                );
              })
            }
          >
            {t("team.copy_last")}
          </Button>
          <Button
            variant="primary"
            icon="send"
            busy={busy === "publish"}
            disabled={!current?.drafts}
            onClick={() =>
              void run("publish", async () => {
                const r = await action<{ published: number }>("team.publish", {
                  start: week,
                  location: loc,
                });
                toast(tn("team.published", r.published));
                refreshTeam();
              })
            }
          >
            {current?.drafts
              ? tn("team.publish_n", current.drafts)
              : t("team.all_published")}
          </Button>
        </div>
      </div>

      {!current ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : current.people.length === 0 ? (
        <EmptyState icon="users" title={t("team.no_people")} />
      ) : (
        <>
          <PlanningBar
            issues={current.issues}
            onSwaps={() => setSwapsOpen(true)}
          />
          <p className="muted small team-rota-legend">
            <span className="rota-chip draft legend">{t("team.draft")}</span>{" "}
            {t("team.draft_hint")} · {t("team.cost", { cost: money(cost) })}
          </p>
          <div
            className="rota-scroll"
            role="region"
            aria-label={t("team.tab_rota")}
            tabIndex={0}
          >
            <table className="rota-grid">
              <thead>
                <tr>
                  <th scope="col">{t("team.c_person")}</th>
                  {current.days.map((d) => (
                    <th
                      key={d}
                      scope="col"
                      className={d === current.today ? "is-today" : undefined}
                    >
                      <span>{weekday(d)}</span>
                      <span className="muted">{shortDay(d)}</span>
                    </th>
                  ))}
                  <th scope="col">{t("team.c_total")}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="rota-open-row">
                  <th scope="row">
                    <span className="rota-person">
                      <strong>{t("plan.open_shifts")}</strong>
                      <span className="muted small">
                        {t("plan.open_row_hint")}
                      </span>
                    </span>
                  </th>
                  {current.days.map((d) => {
                    const cell = current.open_shifts.filter((o) => o.day === d);
                    return (
                      <td
                        key={d}
                        className={d === current.today ? "is-today" : undefined}
                      >
                        <div className="rota-cell">
                          {cell.map((o) => (
                            <button
                              key={o.id}
                              className={`rota-chip open ${o.state}`}
                              onClick={() => setOpenEditing(o)}
                              aria-label={t("plan.edit_open_on", {
                                day: weekday(d, "long"),
                              })}
                            >
                              <strong>
                                {o.start}–{o.end}
                              </strong>
                              {o.position && posById.get(o.position) ? (
                                <PositionTag p={posById.get(o.position)} />
                              ) : (
                                <span>{t("plan.any_position")}</span>
                              )}
                              {o.state === "taken" ? (
                                <span className="rota-taken">
                                  <Icon name="checkCircle" size={12} />{" "}
                                  {o.taken_name}
                                </span>
                              ) : null}
                            </button>
                          ))}
                          <button
                            className="rota-add"
                            aria-label={t("plan.add_open_on", {
                              day: weekday(d, "long"),
                            })}
                            onClick={() =>
                              setOpenEditing({ location: loc, day: d })
                            }
                          >
                            <Icon name="plus" size={16} />
                          </button>
                        </div>
                      </td>
                    );
                  })}
                  <td className="rota-total">
                    {current.issues.open_unfilled
                      ? current.issues.open_unfilled
                      : "—"}
                  </td>
                </tr>
                {current.people.map((p) => (
                  <tr key={p.id}>
                    <th scope="row">
                      <span className="rota-person">
                        <strong>{p.name}</strong>
                        <span className="muted small">
                          {p.roles
                            .map((r) => t(`role.${r}` as MessageKey))
                            .join(", ")}
                        </span>
                      </span>
                    </th>
                    {current.days.map((d) => {
                      const cell = byCell.get(`${p.id}|${d}`) ?? [];
                      const away = elsewhere.get(`${p.id}|${d}`) ?? [];
                      const w = worked.get(`${p.id}|${d}`);
                      return (
                        <td
                          key={d}
                          className={
                            d === current.today ? "is-today" : undefined
                          }
                        >
                          <div className="rota-cell">
                            {cell.map((s) => {
                              const pos = s.position
                                ? posById.get(s.position)
                                : undefined;
                              return (
                                <button
                                  key={s.id}
                                  className={`rota-chip ${s.state}${pos && s.state === "published" ? ` tone-${pos.tone}` : ""}`}
                                  onClick={() => setEditing(s)}
                                  aria-label={t("team.edit_shift_of", {
                                    name: p.name,
                                    day: weekday(d, "long"),
                                  })}
                                >
                                  <strong>
                                    {s.start}–{s.end}
                                  </strong>
                                  {pos && s.state === "draft" ? (
                                    <PositionTag p={pos} />
                                  ) : s.label || pos ? (
                                    <span>{s.label ?? pos?.name}</span>
                                  ) : null}
                                </button>
                              );
                            })}
                            {away.map((s) => (
                              <span
                                key={s.id}
                                className="rota-chip away"
                                title={s.location_name}
                              >
                                <strong>
                                  {s.start}–{s.end}
                                </strong>
                                <span>{placeName(s.location)}</span>
                              </span>
                            ))}
                            <button
                              className="rota-add"
                              aria-label={t("team.add_shift_for", {
                                name: p.name,
                                day: weekday(d, "long"),
                              })}
                              onClick={() =>
                                setEditing({
                                  user: p.id,
                                  day: d,
                                  location: loc,
                                })
                              }
                            >
                              <Icon name="plus" size={16} />
                            </button>
                            {w ? (
                              <span className="rota-worked muted small">
                                {t("team.worked_short", { h: hm(w) })}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      );
                    })}
                    <td
                      className={`rota-total${current.issues.over_limit.some((o) => o.user === p.id) ? " over" : ""}`}
                    >
                      {hm(total(p.id))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {editing && current ? (
        <ShiftDialog
          shift={editing}
          people={current.people}
          positions={current.positions}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}
      {openEditing && current ? (
        <OpenShiftDialog
          open={openEditing}
          positions={current.positions}
          people={current.people}
          onClose={() => setOpenEditing(null)}
          onSaved={async () => {
            setOpenEditing(null);
            await reload();
          }}
        />
      ) : null}
      {positionsOpen && current ? (
        <PositionsDialog
          people={current.people}
          onClose={() => setPositionsOpen(false)}
          onSaved={() => void reload()}
        />
      ) : null}
      {swapsOpen ? (
        <SwapsDialog
          location={loc}
          onClose={() => setSwapsOpen(false)}
          onDone={() => void reload()}
        />
      ) : null}
    </div>
  );
}

const PRESETS: [string, string, MessageKey][] = [
  ["08:00", "16:00", "team.p_morning"],
  ["10:00", "18:00", "team.p_day"],
  ["16:00", "00:00", "team.p_evening"],
];

function ShiftDialog({
  shift,
  people,
  positions,
  onClose,
  onSaved,
}: {
  shift: Partial<PlannedShift>;
  people: { id: string; name: string }[];
  positions: Position[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { run, busy } = useSave();
  const [position, setPosition] = useState(shift.position ?? "");
  const [user, setUser] = useState(shift.user ?? people[0]?.id ?? "");
  const [d, setD] = useState(shift.day ?? todayIso());
  const [start, setStart] = useState(shift.start ?? "10:00");
  const [end, setEnd] = useState(shift.end ?? "18:00");
  const [label, setLabel] = useState(shift.label ?? "");
  const [note, setNote] = useState(shift.note ?? "");
  const [deleting, setDeleting] = useState(false);
  const overnight = end <= start;
  const save = () =>
    void run(
      async () => {
        await action("team.save_shift", {
          id: shift.id ?? null,
          user,
          location: shift.location,
          day: d,
          start,
          end,
          label: label || null,
          position: position || null,
          note: note || null,
        });
        onSaved();
      },
      shift.id ? t("team.shift_saved") : t("team.shift_added"),
    );
  const remove = () =>
    void run(async () => {
      await action("team.delete_shift", { id: shift.id });
      onSaved();
    }, t("team.shift_deleted"));
  return (
    <Dialog
      title={shift.id ? t("team.edit_shift") : t("team.add_shift")}
      onClose={onClose}
      footer={
        <>
          {shift.id ? (
            deleting ? (
              <Button
                variant="danger"
                icon="trash"
                busy={busy}
                onClick={remove}
              >
                {t("team.delete_confirm")}
              </Button>
            ) : (
              <Button
                variant="ghost"
                icon="trash"
                onClick={() => setDeleting(true)}
              >
                {t("common.delete")}
              </Button>
            )
          ) : null}
          <span className="spacer" />
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {shift.state === "published" ? (
        <p className="callout callout-info">
          <Icon name="bell" size={16} /> {t("team.published_hint")}
        </p>
      ) : null}
      <div className="form-grid">
        <Field label={t("team.person")}>
          <select
            className="input"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            disabled={!!shift.id}
          >
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("team.day")}>
          <input
            className="input"
            type="date"
            value={d}
            onChange={(e) => setD(e.target.value)}
          />
        </Field>
        <Field label={t("team.from")}>
          <input
            className="input"
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </Field>
        <Field
          label={t("team.to")}
          hint={overnight && end !== start ? t("team.overnight") : undefined}
        >
          <input
            className="input"
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </Field>
      </div>
      <div className="chips team-presets" aria-label={t("team.presets")}>
        {PRESETS.map(([a, b, k]) => (
          <button
            key={k}
            className={`chip${start === a && end === b ? " active" : ""}`}
            onClick={() => {
              setStart(a);
              setEnd(b);
            }}
          >
            {t(k)} · {a}–{b}
          </button>
        ))}
      </div>
      {positions.length ? (
        <Field
          label={t("plan.position")}
          hint={(() => {
            const pos = positions.find((p) => p.id === position);
            return pos && pos.users.length && !pos.users.includes(user)
              ? t("plan.not_named")
              : undefined;
          })()}
        >
          <select
            className="input"
            value={position}
            onChange={(e) => {
              const old = positions.find((p) => p.id === position)?.name ?? "";
              const next =
                positions.find((p) => p.id === e.target.value)?.name ?? "";
              // The label follows the position unless someone typed their own.
              if (!label || label === old) setLabel(next);
              setPosition(e.target.value);
            }}
          >
            <option value="">{t("plan.no_position")}</option>
            {positions
              .filter((p) => p.active || p.id === position)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
      ) : null}
      <Field label={t("team.label")} hint={t("team.label_hint")}>
        <input
          className="input"
          value={label}
          maxLength={40}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <Field label={t("team.note")}>
        <input
          className="input"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}

// ---- hours -------------------------------------------------------------------------------

interface EntryRow {
  id: string;
  user: string;
  name: string;
  location: string;
  location_name: string;
  day: string;
  in_at: string;
  out_at: string | null;
  break_minutes: number;
  minutes: number;
  planned: { start: string; end: string } | null;
  late_minutes: number;
  long: boolean;
  state: "active" | "void";
  edit_reason: string | null;
  edited_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  void_reason: string | null;
}

/** Week presets and a custom range in the address, shared by the hours, tips and payroll tabs. */
function usePeriod(defaultPreset: "week" | "month") {
  const [params, setParams] = useSearchParams();
  const today = todayIso();
  const presets =
    defaultPreset === "week"
      ? (["week", "last_week", "month"] as const)
      : (["month", "last_month", "week"] as const);
  type P = (typeof presets)[number] | "last_month" | "last_week" | "custom";
  const p = ((params.get("p") as P) ?? defaultPreset) as P;
  const monday = mondayOf(today);
  const firstOfMonth = `${today.slice(0, 8)}01`;
  const lastMonthEnd = addDays(firstOfMonth, -1);
  const ranges: Record<Exclude<P, "custom">, { from: string; to: string }> = {
    week: { from: monday, to: addDays(monday, 6) },
    last_week: { from: addDays(monday, -7), to: addDays(monday, -1) },
    month: { from: firstOfMonth, to: today },
    last_month: { from: `${lastMonthEnd.slice(0, 8)}01`, to: lastMonthEnd },
  };
  const range =
    p === "custom"
      ? {
          from: isDay(params.get("from")) ? params.get("from")! : monday,
          to: isDay(params.get("to")) ? params.get("to")! : today,
        }
      : (ranges[p as Exclude<P, "custom">] ?? ranges[defaultPreset]);
  const set = (next: Record<string, string | null>) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) q.delete(k);
      else q.set(k, v);
    }
    setParams(q, { replace: true });
  };
  const bar = (
    <div className="toolbar-group">
      <div className="segmented" role="radiogroup" aria-label={t("rep.period")}>
        {[...presets, "custom" as const].map((x) => (
          <button
            key={x}
            role="radio"
            aria-checked={p === x}
            className={p === x ? "active" : ""}
            onClick={() =>
              set(
                x === "custom"
                  ? { p: x, from: range.from, to: range.to }
                  : { p: x, from: null, to: null },
              )
            }
          >
            {t(`team.p_${x}` as MessageKey)}
          </button>
        ))}
      </div>
      {p === "custom" ? (
        <span className="toolbar-group">
          <input
            className="input date-input"
            type="date"
            aria-label={t("rep.from")}
            value={range.from}
            onChange={(e) =>
              isDay(e.target.value) && set({ from: e.target.value })
            }
          />
          <input
            className="input date-input"
            type="date"
            aria-label={t("rep.to")}
            value={range.to}
            onChange={(e) =>
              isDay(e.target.value) && set({ to: e.target.value })
            }
          />
        </span>
      ) : null}
    </div>
  );
  return { range, bar, valid: range.from <= range.to };
}

function Hours() {
  const { can } = useSession();
  const { multi, here } = usePlaces();
  const { fail, toast } = useUi();
  const { range, bar, valid } = usePeriod("week");
  const [user, setUser] = useState("");
  const [editing, setEditing] = useState<Partial<EntryRow> | null>(null);
  const [busy, setBusy] = useState(false);
  const input = valid
    ? {
        from: range.from,
        to: range.to,
        user: user || null,
        location: multi && here ? here.id : null,
      }
    : { from: range.from, to: range.from };
  const { data, reload } = useLiveAction<{
    entries: EntryRow[];
    people: { id: string; name: string }[];
  }>("team.entries", input, ["team"]);
  const manage = can("team.manage");
  const rows = data?.entries ?? [];
  const toApprove = rows.filter(
    (e) => e.state === "active" && e.out_at && !e.approved_at,
  );
  const total = rows
    .filter((e) => e.state === "active")
    .reduce((a, e) => a + e.minutes, 0);

  const approveAll = async () => {
    setBusy(true);
    try {
      const r = await action<{ approved: number }>("team.approve", {
        ids: toApprove.map((e) => e.id),
      });
      toast(tn("team.approved", r.approved));
      await reload();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="team-hours">
      <div className="toolbar">
        {bar}
        <div className="toolbar-group toolbar-end">
          <select
            className="input"
            aria-label={t("team.person")}
            value={user}
            onChange={(e) => setUser(e.target.value)}
          >
            <option value="">{t("team.everyone")}</option>
            {(data?.people ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {manage ? (
            <>
              <Button
                icon="plus"
                onClick={() =>
                  setEditing({ user: user || undefined, location: here?.id })
                }
              >
                {t("team.add_hours")}
              </Button>
              <Button
                variant="primary"
                icon="checkCircle"
                busy={busy}
                disabled={!toApprove.length}
                onClick={() => void approveAll()}
              >
                {toApprove.length
                  ? tn("team.approve_n", toApprove.length)
                  : t("team.all_approved")}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <section className="card">
          <WideTable
            head={[
              t("team.c_person"),
              t("team.c_day"),
              t("team.c_in_out"),
              t("team.c_planned"),
              t("team.c_break"),
              t("team.c_worked"),
            ]}
            left={[1, 2]}
            phoneHide={[3, 4]}
            empty={t("team.no_hours")}
            foot={
              rows.length ? [t("team.total"), "", "", "", "", hm(total)] : null
            }
            rows={rows.map((e) => ({
              key: e.id,
              onClick:
                manage && e.state === "active"
                  ? () => setEditing(e)
                  : undefined,
              cells: [
                <span className="team-who">
                  <span>{e.name}</span>
                  <EntryBadges e={e} />
                </span>,
                `${weekday(e.day)} ${shortDay(e.day)}${multi && !here ? ` · ${e.location_name}` : ""}`,
                <span className={e.state === "void" ? "struck" : undefined}>
                  {clock(e.in_at)}–{e.out_at ? clock(e.out_at) : t("team.now")}
                </span>,
                e.planned ? `${e.planned.start}–${e.planned.end}` : "—",
                e.break_minutes ? hm(e.break_minutes) : "—",
                e.state === "void" ? "—" : hm(e.minutes),
              ],
            }))}
          />
        </section>
      )}
      {editing ? (
        <EntryDialog
          entry={editing}
          people={data?.people ?? []}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}

function EntryBadges({ e }: { e: EntryRow }) {
  const out: ReactNode[] = [];
  if (e.state === "void")
    out.push(
      <StatusBadge key="v" tone="neutral" icon="x" label={t("team.b_void")} />,
    );
  else {
    if (!e.out_at)
      out.push(
        <StatusBadge
          key="o"
          tone={e.long ? "danger" : "ok"}
          icon={e.long ? "alert" : "clock"}
          label={e.long ? t("team.b_forgot") : t("team.b_in")}
          pulse={!e.long}
        />,
      );
    if (e.late_minutes)
      out.push(
        <StatusBadge
          key="l"
          tone="warn"
          icon="alert"
          label={t("team.b_late", { n: e.late_minutes })}
        />,
      );
    if (e.edit_reason)
      out.push(
        <StatusBadge
          key="e"
          tone="info"
          icon="edit"
          label={t("team.b_edited")}
        />,
      );
    if (e.approved_at)
      out.push(
        <StatusBadge
          key="a"
          tone="ok"
          icon="check"
          label={t("team.b_approved")}
        />,
      );
  }
  return (
    <span
      className="badge-row"
      title={
        [e.edit_reason, e.void_reason].filter(Boolean).join(" · ") || undefined
      }
    >
      {out}
    </span>
  );
}

function EntryDialog({
  entry,
  people,
  onClose,
  onSaved,
}: {
  entry: Partial<EntryRow>;
  people: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { run, busy } = useSave();
  const { places, multi } = usePlaces();
  const [user, setUser] = useState(entry.user ?? people[0]?.id ?? "");
  const [location, setLocation] = useState(
    entry.location ?? places[0]?.id ?? "",
  );
  const [inAt, setInAt] = useState(
    entry.in_at ? toLocalInput(entry.in_at) : `${todayIso()}T09:00`,
  );
  const [outAt, setOutAt] = useState(
    entry.id
      ? entry.out_at
        ? toLocalInput(entry.out_at)
        : ""
      : `${todayIso()}T17:00`,
  );
  const [brk, setBrk] = useState(String(entry.break_minutes ?? 0));
  const [reason, setReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const minutes =
    outAt && inAt
      ? Math.round(
          (new Date(outAt).getTime() - new Date(inAt).getTime()) / 60_000,
        ) - Number(brk || 0)
      : null;
  const save = () =>
    void run(async () => {
      await action("team.save_entry", {
        id: entry.id ?? null,
        user,
        location,
        in_at: new Date(inAt).toISOString(),
        out_at: outAt ? new Date(outAt).toISOString() : null,
        break_minutes: Number(brk || 0),
        reason,
      });
      onSaved();
    }, t("team.hours_saved"));
  const remove = () =>
    void run(async () => {
      await action("team.void_entry", { id: entry.id, reason });
      onSaved();
    }, t("team.hours_removed"));
  return (
    <Dialog
      title={
        entry.id
          ? t("team.fix_hours", { name: entry.name ?? "" })
          : t("team.add_hours")
      }
      onClose={onClose}
      footer={
        <>
          {entry.id ? (
            <Button
              variant={voiding ? "danger" : "ghost"}
              icon="trash"
              busy={voiding && busy}
              disabled={reason.trim().length < 3}
              onClick={() => (voiding ? remove() : setVoiding(true))}
            >
              {voiding ? t("team.remove_confirm") : t("team.remove")}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={!voiding && busy}
            disabled={reason.trim().length < 3}
            onClick={save}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      {entry.edit_reason ? (
        <p className="callout callout-info">
          <Icon name="edit" size={16} />{" "}
          {t("team.changed_by", {
            name: entry.edited_by_name ?? "",
            reason: entry.edit_reason,
          })}
        </p>
      ) : null}
      <div className="form-grid">
        <Field label={t("team.person")}>
          <select
            className="input"
            value={user}
            disabled={!!entry.id}
            onChange={(e) => setUser(e.target.value)}
          >
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {multi ? (
          <Field label={t("team.location")}>
            <select
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            >
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label={t("team.clock_in_at")}>
          <input
            className="input"
            type="datetime-local"
            value={inAt}
            onChange={(e) => setInAt(e.target.value)}
          />
        </Field>
        <Field
          label={t("team.clock_out_at")}
          hint={!outAt ? t("team.still_in") : undefined}
        >
          <input
            className="input"
            type="datetime-local"
            value={outAt}
            onChange={(e) => setOutAt(e.target.value)}
          />
        </Field>
        <Field label={t("team.break_minutes")}>
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min={0}
            max={600}
            value={brk}
            onChange={(e) => setBrk(e.target.value)}
          />
        </Field>
        <Field label={t("team.c_worked")}>
          <output className="input input-static">
            {minutes === null
              ? "—"
              : minutes > 0
                ? hm(minutes)
                : t("team.bad_times")}
          </output>
        </Field>
      </div>
      <Field label={t("team.reason")} hint={t("team.reason_hint")}>
        <input
          className="input"
          value={reason}
          maxLength={200}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("team.reason_example")}
        />
      </Field>
    </Dialog>
  );
}

// ---- tips --------------------------------------------------------------------------------

interface TipRow {
  user: string;
  name: string;
  roles: string[];
  active: boolean;
  takes_tips: boolean;
  earned: number;
  paid_cash: number;
  paid_payroll: number;
  owed: number;
}
interface TipsData {
  mode: "own" | "pool";
  people: TipRow[];
  total: { earned: number; owed: number };
  payouts: {
    id: string;
    user: string;
    name: string;
    amount: number;
    method: "cash" | "payroll";
    note: string | null;
    at: string;
    by_name: string | null;
    register: string | null;
  }[];
}

function Tips() {
  const { multi, here } = usePlaces();
  const { range, bar, valid } = usePeriod("week");
  const { data, reload } = useLiveAction<TipsData>(
    "team.tips",
    valid
      ? {
          from: range.from,
          to: range.to,
          location: multi && here ? here.id : null,
        }
      : { from: range.from, to: range.from },
    ["team", "sales.order"],
  );
  const [paying, setPaying] = useState<TipRow | null>(null);
  const shift = useDeviceShift();
  return (
    <div className="team-tips">
      <div className="toolbar">{bar}</div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          <div className="rep-kpis team-kpis">
            <Kpi
              label={t("team.k_tips_period")}
              value={money(data.total.earned)}
              hint={
                data.mode === "pool" ? t("team.pool_hint") : t("team.own_hint")
              }
            />
            <Kpi
              label={t("team.k_tips_owed_all")}
              value={money(data.total.owed)}
              hint={t("team.owed_all_hint")}
            />
          </div>
          <section className="card">
            <WideTable
              head={[
                t("team.c_person"),
                t("team.c_earned"),
                t("team.c_paid_cash"),
                t("team.c_paid_payroll"),
                t("team.c_owed"),
                "",
              ]}
              phoneHide={[2, 3]}
              empty={t("team.no_tips")}
              rows={data.people.map((r) => ({
                key: r.user,
                cells: [
                  <span className="rota-person">
                    <strong>{r.name}</strong>
                    {!r.takes_tips ? (
                      <span className="muted small">
                        {t("team.no_tips_person")}
                      </span>
                    ) : null}
                  </span>,
                  r.earned ? money(r.earned) : "—",
                  r.paid_cash ? money(r.paid_cash) : "—",
                  r.paid_payroll ? money(r.paid_payroll) : "—",
                  <strong>{r.owed > 0 ? money(r.owed) : "—"}</strong>,
                  r.owed > 0 ? (
                    <Button size="sm" icon="cash" onClick={() => setPaying(r)}>
                      {t("team.pay")}
                    </Button>
                  ) : (
                    ""
                  ),
                ],
              }))}
            />
          </section>
          {data.payouts.length ? (
            <section className="card">
              <header className="card-head">
                <h2>{t("team.payouts")}</h2>
              </header>
              <WideTable
                head={[
                  t("team.c_when"),
                  t("team.c_person"),
                  t("team.c_how"),
                  t("team.c_amount"),
                ]}
                left={[1, 2]}
                rows={data.payouts.map((p) => ({
                  key: p.id,
                  cells: [
                    `${shortDay(p.at.slice(0, 10))} ${clock(p.at)}`,
                    p.name,
                    p.method === "cash"
                      ? t("team.from_drawer", { register: p.register ?? "" })
                      : t("team.with_payroll"),
                    money(p.amount),
                  ],
                }))}
              />
            </section>
          ) : null}
        </>
      )}
      {paying ? (
        <PayTipsDialog
          person={paying}
          session={shift}
          onClose={() => setPaying(null)}
          onPaid={async () => {
            setPaying(null);
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** The open shift on this device's register, if there is one, to pay tips from its drawer. */
function useDeviceShift(): { id: string; register: string } | null {
  const [shift, setShift] = useState<{ id: string; register: string } | null>(
    null,
  );
  useEffect(() => {
    const register = device.register;
    if (!register) return;
    action<{ session: { id: string } | null; register?: { name: string } }>(
      "pos.current_shift",
      { register },
    )
      .then((r) => setShift(r.session ? { id: r.session.id, register } : null))
      .catch(() => setShift(null));
  }, []);
  return shift;
}

export function PayTipsDialog({
  person,
  session,
  drawer,
  onClose,
  onPaid,
}: {
  person: { user: string; name: string; owed: number } | null;
  session: { id: string } | null;
  /** Cash in the drawer, when known: tips paid from it cannot be more. */
  drawer?: number;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { can } = useSession();
  const { run, busy } = useSave();
  const { fail } = useUi();
  // Paying from the till: the cashier picks the person from those owed tips.
  const [owedList, setOwedList] = useState<TipsData | null>(null);
  useEffect(() => {
    if (person) return;
    action<TipsData>("team.tips", { from: todayIso(), to: todayIso() }).then(
      setOwedList,
      fail,
    );
  }, [person, fail]);
  const choices = person
    ? [person]
    : (owedList?.people ?? []).filter((p) => p.owed > 0);
  const [who, setWho] = useState(person?.user ?? "");
  const picked =
    choices.find((c) => c.user === who) ??
    (choices.length === 1 ? choices[0] : undefined);
  const [method, setMethod] = useState<"cash" | "payroll">(
    session ? "cash" : "payroll",
  );
  const [typed, setTyped] = useState("");
  const amount = typed ? parseMoney(typed) : (picked?.owed ?? null);
  const payroll = can("team.manage");
  const tooMuch = picked && amount !== null && amount > picked.owed;
  const short =
    method === "cash" &&
    drawer !== undefined &&
    amount !== null &&
    amount > drawer;
  const pay = () =>
    void run(
      async () => {
        await action("team.pay_tips", {
          user: picked!.user,
          amount,
          method,
          session: method === "cash" ? session?.id : null,
        });
        onPaid();
      },
      t("team.paid_toast", {
        amount: money(amount ?? 0),
        name: picked?.name ?? "",
      }),
    );
  return (
    <Dialog
      title={
        picked ? t("team.pay_title", { name: picked.name }) : t("team.pay_tips")
      }
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="cash"
            busy={busy}
            disabled={
              !picked ||
              !amount ||
              amount <= 0 ||
              !!tooMuch ||
              short ||
              (method === "cash" && !session) ||
              (method === "payroll" && !payroll)
            }
            onClick={pay}
          >
            {t("team.pay_amount", { amount: money(amount ?? 0) })}
          </Button>
        </>
      }
    >
      {!person ? (
        choices.length ? (
          <Field label={t("team.person")}>
            <select
              className="input"
              value={picked?.user ?? ""}
              onChange={(e) => setWho(e.target.value)}
            >
              <option value="" disabled>
                {t("team.choose_person")}
              </option>
              {choices.map((c) => (
                <option key={c.user} value={c.user}>
                  {c.name} · {money(c.owed)}
                </option>
              ))}
            </select>
          </Field>
        ) : owedList ? (
          <EmptyState icon="cash" title={t("team.nothing_owed")} />
        ) : (
          <Skeleton h={48} />
        )
      ) : null}
      {picked ? (
        <>
          <p className="team-owed">
            {t("team.owed_now")} <strong>{money(picked.owed)}</strong>
          </p>
          {payroll ? (
            <div
              className="segmented chips-row"
              role="radiogroup"
              aria-label={t("team.c_how")}
            >
              <button
                role="radio"
                aria-checked={method === "cash"}
                className={method === "cash" ? "active" : ""}
                disabled={!session}
                onClick={() => setMethod("cash")}
              >
                <Icon name="register" size={16} /> {t("team.m_cash")}
              </button>
              <button
                role="radio"
                aria-checked={method === "payroll"}
                className={method === "payroll" ? "active" : ""}
                onClick={() => setMethod("payroll")}
              >
                <Icon name="book" size={16} /> {t("team.m_payroll")}
              </button>
            </div>
          ) : null}
          {method === "cash" && !session ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} /> {t("team.no_drawer")}
            </p>
          ) : (
            <p className="muted small">
              {method === "cash" ? t("team.cash_hint") : t("team.payroll_hint")}
              {method === "cash" && drawer !== undefined
                ? ` ${t("team.in_drawer", { amount: money(drawer) })}`
                : ""}
            </p>
          )}
          <Field
            label={t("team.amount")}
            error={
              tooMuch
                ? t("team.too_much")
                : short
                  ? t("team.drawer_short", { amount: money(drawer!) })
                  : null
            }
          >
            <input
              className="input"
              inputMode="decimal"
              placeholder={plain(picked.owed)}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </Field>
        </>
      ) : null}
    </Dialog>
  );
}

// ---- payroll -----------------------------------------------------------------------------

interface PayrollRow {
  user: string;
  name: string;
  username: string;
  payroll_id: string | null;
  days: number;
  planned_minutes: number;
  worked_minutes: number;
  overtime_minutes: number;
  open_entries: number;
  unapproved: number;
  hourly_rate: number;
  pay: number;
  tips_earned: number;
  tips_paid_cash: number;
  tips_to_payroll: number;
  tips_owed: number;
}
interface PayrollData {
  from: string;
  to: string;
  overtime_after: number;
  rows: PayrollRow[];
  totals: {
    worked_minutes: number;
    overtime_minutes: number;
    pay: number;
    tips_earned: number;
    tips_to_payroll: number;
    open_entries: number;
    unapproved: number;
  };
}

function Payroll() {
  const { range, bar, valid } = usePeriod("month");
  const [, setParams] = useSearchParams();
  const { data } = useAction<PayrollData>(
    "team.payroll",
    valid ? range : { from: range.from, to: range.from },
  );
  const current =
    data && data.from === range.from && data.to === range.to ? data : null;
  const download = () =>
    current &&
    downloadCsv(
      `payroll-${range.from}-${range.to}.csv`,
      [
        t("team.c_payroll_id"),
        t("team.c_person"),
        t("team.c_days"),
        t("team.csv_planned"),
        t("team.csv_worked"),
        t("team.csv_overtime"),
        t("team.c_rate"),
        t("team.c_pay"),
        t("team.c_earned"),
        t("team.c_paid_cash"),
        t("team.c_paid_payroll"),
        t("team.c_owed"),
      ],
      current.rows.map((r) => [
        r.payroll_id ?? r.username,
        r.name,
        r.days,
        hours(r.planned_minutes),
        hours(r.worked_minutes),
        hours(r.overtime_minutes),
        plain(r.hourly_rate),
        plain(r.pay),
        plain(r.tips_earned),
        plain(r.tips_paid_cash),
        plain(r.tips_to_payroll),
        plain(Math.max(0, r.tips_owed)),
      ]),
    );
  return (
    <div className="team-payroll">
      <div className="toolbar">
        {bar}
        <div className="toolbar-group toolbar-end">
          <Button
            variant="secondary"
            icon="download"
            disabled={!current?.rows.length}
            onClick={download}
          >
            {t("rep.download")}
          </Button>
        </div>
      </div>
      {!current ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          {current.totals.open_entries || current.totals.unapproved ? (
            <p className="callout callout-warn" role="status">
              <Icon name="alert" size={16} />
              <span>
                {[
                  current.totals.open_entries
                    ? tn("team.w_open", current.totals.open_entries)
                    : null,
                  current.totals.unapproved
                    ? tn("team.w_unapproved", current.totals.unapproved)
                    : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setParams(
                    new URLSearchParams({
                      tab: "hours",
                      p: "custom",
                      from: range.from,
                      to: range.to,
                    }),
                    { replace: true },
                  )
                }
              >
                {t("team.check_hours")}
              </Button>
            </p>
          ) : null}
          <div className="rep-kpis team-kpis">
            <Kpi
              label={t("team.k_worked")}
              value={hm(current.totals.worked_minutes)}
              hint={
                current.totals.overtime_minutes
                  ? t("team.k_overtime", {
                      h: hm(current.totals.overtime_minutes),
                    })
                  : undefined
              }
            />
            <Kpi
              label={t("team.k_pay")}
              value={money(current.totals.pay)}
              hint={t("team.pay_hint")}
            />
            <Kpi
              label={t("team.k_tips_payroll")}
              value={money(current.totals.tips_to_payroll)}
            />
          </div>
          <section className="card">
            <WideTable
              head={[
                t("team.c_person"),
                t("team.c_days"),
                t("team.c_worked"),
                t("team.c_overtime"),
                t("team.c_rate"),
                t("team.c_pay"),
                t("team.c_earned"),
                t("team.c_owed"),
              ]}
              phoneHide={[1, 3, 4, 6]}
              empty={t("team.no_payroll")}
              foot={
                current.rows.length
                  ? [
                      t("team.total"),
                      "",
                      hm(current.totals.worked_minutes),
                      hm(current.totals.overtime_minutes),
                      "",
                      money(current.totals.pay),
                      money(current.totals.tips_earned),
                      "",
                    ]
                  : null
              }
              rows={current.rows.map((r) => ({
                key: r.user,
                cells: [
                  <span className="rota-person">
                    <strong>{r.name}</strong>
                    <span className="muted small">
                      {r.payroll_id ?? t("team.no_payroll_id")}
                      {r.open_entries || r.unapproved ? " · " : ""}
                      {r.open_entries
                        ? tn("team.w_open_short", r.open_entries)
                        : ""}
                      {r.unapproved
                        ? ` ${tn("team.w_unapproved_short", r.unapproved)}`
                        : ""}
                    </span>
                  </span>,
                  r.days || "—",
                  hm(r.worked_minutes),
                  hm(r.overtime_minutes),
                  r.hourly_rate ? money(r.hourly_rate) : "—",
                  r.pay ? money(r.pay) : "—",
                  r.tips_earned ? money(r.tips_earned) : "—",
                  r.tips_owed > 0 ? money(r.tips_owed) : "—",
                ],
              }))}
            />
          </section>
          <p className="muted small">
            {t("team.payroll_note", { h: hm(current.overtime_after) })}
          </p>
        </>
      )}
    </div>
  );
}
